import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaFilePdf } from 'react-icons/fa';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Paper, Dialog, FormControl, InputLabel, MenuItem, Select,
  DialogActions, DialogContent, DialogTitle, Typography, TablePagination,
  TextField, Box, TableSortLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Debounce hook: value ancak delay ms boyunca değişmezse güncellenir
function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // MUI TablePagination 0-based
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [userToDelete, setUserToDelete] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  // Filters (default: only notverified)
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [filterStatus, setFilterStatus] = useState('notverified');
  const [filterDate, setFilterDate] = useState(''); // YYYY-MM-DD

  // Debounced values (API bunlarla çağrılacak)
  const debouncedSearchName = useDebounce(searchName, 500);
  const debouncedSearchEmail = useDebounce(searchEmail, 500);

  // Server-side sorting
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');

  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const params = {
        page: page + 1,       // API 1-based
        limit: rowsPerPage,   // default 10
        name: debouncedSearchName || "",
        email: debouncedSearchEmail || "",
        status: filterStatus || "",
        date: filterDate || "",
        orderBy,
        order,
      };

      const response = await axios.get('https://kariyer.biyomed.net/api/users/getAll', { params });

      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Filtre/sort/limit değişince sayfayı sıfırla
  // (search alanlarında debounced değerleri kullanıyoruz ki her tuşta tetiklenmesin)
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchName, debouncedSearchEmail, filterStatus, filterDate, order, orderBy, rowsPerPage]);

  // Fetch on any relevant change (server-side pagination/filter/sort)
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearchName, debouncedSearchEmail, filterStatus, filterDate, order, orderBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleOpenDialog = (user) => {
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = (pdfData, fileName) => {
    if (!pdfData) {
      alert("PDF belgesi bulunamadı.");
      return;
    }
    try {
      const base64Data = pdfData.startsWith('data:application/pdf;base64,')
        ? pdfData.split('base64,')[1]
        : pdfData;

      const byteArray = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF indirme hatası:", error);
      alert("PDF indirilirken bir hata oluştu.");
    }
  };

  const handleApprove = async (userId, userEmail) => {
    try {
      await axios.put('https://kariyer.biyomed.net/api/users/updateStatus', {
        user_id: userId,
        user_email: userEmail,
      });
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Onaylama işlemi sırasında bir hata oluştu.');
    }
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setDeleteReason('');
    setOpenConfirmDialog(true);
  };

  const handleDeleteUser = async (e, userId, Email) => {
    e.preventDefault();
    try {
      await axios.post(
        'https://kariyer.biyomed.net/api/admin/deleteUser',
        { user_id: userId, email: Email, rejectReason: deleteReason },
        { withCredentials: true }
      );
      await fetchUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Bilinmeyen bir hata oluştu.';
      console.error('Hesap silme işlemi başarısız:', error);
      alert(`Hesap silme işlemi başarısız: ${errorMessage}`);
    } finally {
      setOpenConfirmDialog(false);
      setUserToDelete(null);
      setDeleteReason('');
    }
  };

  const emptyRows = Math.max(0, rowsPerPage - (users?.length || 0));

  if (loading) {
    return <div>Yükleniyor Lütfen bekleyiniz...</div>;
  }

  return (
    <div>
      <h2 style={{ marginBottom: 30 }}>Kişi kayıt listesi</h2>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          label="İsimle Ara"
          variant="outlined"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          sx={{ width: 200 }}
        />
        <TextField
          label="E-posta ile Ara"
          variant="outlined"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          sx={{ width: 200 }}
        />
        <FormControl sx={{ width: 200 }}>
          <InputLabel>Kayıt Durumu</InputLabel>
          <Select
            value={filterStatus}
            label="Kayıt Durumu"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="notverified">Onaylanmadı</MenuItem>
            <MenuItem value="verified">Onaylandı</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Kayıt Tarihi"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          sx={{ width: 200 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Kullanıcı ID</TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === 'first_name'}
                  direction={orderBy === 'first_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('first_name')}
                >
                  Ad
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === 'last_name'}
                  direction={orderBy === 'last_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('last_name')}
                >
                  Soyad
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  E-posta
                </TableSortLabel>
              </TableCell>

              <TableCell>Telefon</TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === 'program'}
                  direction={orderBy === 'program' ? order : 'asc'}
                  onClick={() => handleRequestSort('program')}
                >
                  Lisans
                </TableSortLabel>
              </TableCell>

              <TableCell>Mezun/Öğrenci</TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Kayıt Durumu
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleRequestSort('created_at')}
                >
                  Kayıt Tarihi
                </TableSortLabel>
              </TableCell>

              <TableCell>Detay Bilgileri</TableCell>
              <TableCell>Mezun/Öğrenci Belgesi</TableCell>
              <TableCell>İşlemler</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.first_name}</TableCell>
                <TableCell>{user.last_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.phone}</TableCell>
                <TableCell>{user.program}</TableCell>
                <TableCell>{user.user_type}</TableCell>

                <TableCell sx={{ color: `${user.status === 'notverified' ? 'red' : 'green'}`, fontWeight: "bold" }}>
                  {user.status === 'notverified' ? 'Onaylanmadi' : "Onaylandi"}
                </TableCell>

                <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>

                <TableCell style={{ color: user.details === 0 ? "red" : "green", fontWeight: "bold" }}>
                  {user.details === 0 ? "Girilmemiş" : "Girilmiş"}
                </TableCell>

                <TableCell>
                  {user.graduation_document && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => handleDownload(user.graduation_document, `${user.first_name}_graduation_document.pdf`)}
                      startIcon={<FaFilePdf />}
                    >
                      Belgeyi İndir
                    </Button>
                  )}
                </TableCell>

                <TableCell>
                  {user.status === 'notverified' ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleApprove(user.id, user.email)}
                      >
                        Onayla
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => confirmDelete(user)}
                      >
                        Reddet ve Sil
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="info"
                        onClick={() => handleOpenDialog(user)}
                      >
                        Detaylar
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => confirmDelete(user)}
                      >
                        Sil
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={12} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedUser && (
        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>Kullanıcı Detayları</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              <strong>Oluşturma Tarihi:</strong> {new Date(selectedUser.created_at).toLocaleDateString()}
            </Typography>
            {selectedUser.temp_password ? (
              <Typography variant="body1"><strong>Geçici Şifre:</strong> {selectedUser.temp_password}</Typography>
            ) : (
              <Typography color="warning">Kullanıcı kendi şifresini oluşturdu.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">Kapat</Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => navigate(`/admin/kullanici-detay/${selectedUser?.id}`)}
              disabled={selectedUser.details === 0}
            >
              Detay Bilgileri Görüntüle
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Hesabı Sil</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Bu kullanıcıyı silmek istediğinize emin misiniz?
          </Typography>
          <TextField
            label="Reddedilme veya silinme sebebini belirt"
            placeholder="Sebep yazmazsanız sistem varsayılan mesajı gönderir"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenConfirmDialog(false);
              setUserToDelete(null);
              setDeleteReason('');
            }}
            color="primary"
          >
            İptal
          </Button>
          {userToDelete && (
            <Button onClick={(e) => handleDeleteUser(e, userToDelete.id, userToDelete.email)} color="error">
              Sil
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Sayfa başına satır:"
      />
    </div>
  );
};

export default UserList;